import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { fine, money } from '../../utils/format';
import { ErrorNotice } from '../../components/ui';
import { Drawer, Field, TextInput } from '../../components/ui/Form';
import type { ChitDetail, Instalment } from './ChitsPage';

type Mode = 'redeem' | 'break' | 'undo';

/**
 * The three ways an account's money moves out of the ordinary monthly rhythm.
 *
 *  - Redeem: a matured account's savings, bonus and — on a gold scheme — the
 *    rise in value become the customer's advance, spent at the till like cash.
 *  - Close early: they get back what they paid, less the scheme's penalty, and
 *    no bonus. The shop can waive the penalty, and the refund can be kept as an
 *    advance instead of paid out.
 *  - Undo: the last payment was a mistake or a bounced cheque. It is reversed in
 *    the books, not deleted, so the day book still shows it came and went.
 *
 * Every figure is shown before the button is pressed; none of these can be
 * taken back afterwards.
 */
export function SettleDrawer({
  mode,
  account,
  onClose,
}: {
  mode: Mode;
  account: ChitDetail & { instalments: Instalment[] };
  onClose: () => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  const [reason, setReason] = useState('');
  const [refundMode, setRefundMode] = useState<'CASH' | 'BANK' | 'ADVANCE'>('ADVANCE');
  const [waive, setWaive] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const st = account.settlement;
  const lastPaid = [...account.instalments].reverse().find((i) => i.status === 'PAID');

  const refresh = (): void => {
    for (const k of ['chit-account', 'chit-accounts', 'chit-overview', 'chit-due']) void client.invalidateQueries({ queryKey: [k] });
  };

  const act = useMutation({
    mutationFn: async () => {
      if (mode === 'redeem') return api.post(`/chit/accounts/${account.id}/redeem`, {});
      if (mode === 'undo') return api.post(`/chit/accounts/${account.id}/undo`, { reason: reason.trim() });
      return api.post(`/chit/accounts/${account.id}/break`, { reason: reason.trim(), refundMode, waivePenalty: waive });
    },
    onSuccess: () => {
      refresh();
      if (mode === 'redeem') {
        setRedeemed(true);
        return;
      }
      toast(
        mode === 'undo' ? `Instalment ${lastPaid?.instalment_no} undone` : `${account.chit_no} closed early`,
        mode === 'undo' ? 'Reversed in the books' : refundMode === 'ADVANCE' ? 'Refund kept on the customer’s account' : 'Pay the refund out',
      );
      onClose();
    },
    onError: setError,
  });

  const penalty = waive ? 0 : Number(st.breakPenalty);
  const refund = Number(st.paid) - penalty;

  const titles: Record<Mode, string> = {
    redeem: 'Redeem savings',
    break: 'Close early',
    undo: 'Undo last payment',
  };

  if (redeemed) {
    return (
      <Drawer title="Savings redeemed" subtitle={account.customer_name} onClose={onClose}>
        <div className="space-y-4 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold-100 text-2xl text-gold-700">★</div>
          <p className="num text-2xl font-bold">{money(st.redeemable)}</p>
          <p className="text-sm text-ink-soft">
            is now on {account.customer_name}’s account. On the Sell screen, choose this customer and pay with
            “Advance”.
          </p>
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={() => navigate('/pos')}>
              Go to Sell
            </button>
            <button className="btn-ghost flex-1" onClick={onClose}>
              Later
            </button>
          </div>
        </div>
      </Drawer>
    );
  }

  const ready = mode === 'redeem' || reason.trim().length >= 3;

  return (
    <Drawer
      title={titles[mode]}
      subtitle={`${account.customer_name} · ${account.chit_no}`}
      onClose={onClose}
      footer={
        <button
          className={`w-full ${mode === 'break' ? 'btn-danger' : 'btn-primary'}`}
          disabled={!ready || act.isPending}
          onClick={() => act.mutate()}
        >
          {act.isPending
            ? 'Working…'
            : mode === 'redeem'
              ? `Redeem ${money(st.redeemable)} to the customer’s account`
              : mode === 'break'
                ? `Close and refund ${money(refund.toFixed(2))}`
                : `Undo instalment ${lastPaid?.instalment_no ?? ''}`}
        </button>
      }
    >
      <ErrorNotice error={error} />

      {mode === 'redeem' ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-silver-100 p-3 text-sm">
            <Row label={`Paid (${account.paid_count} instalments)`} value={money(st.paid)} />
            {account.scheme_type === 'WEIGHT' ? (
              <>
                <Row label="Gold saved" value={fine(st.paidWeight)} />
                <Row label="Today’s rate" value={st.currentRate ? `${money(st.currentRate)}/g` : '—'} />
                <Row
                  label={Number(st.rateBenefit) >= 0 ? 'Rise in gold value' : 'Fall in gold value'}
                  value={`${Number(st.rateBenefit) >= 0 ? '+' : ''} ${money(st.rateBenefit)}`}
                  tone={Number(st.rateBenefit) >= 0 ? 'good' : 'bad'}
                />
              </>
            ) : null}
            <Row label="Shop’s bonus" value={`+ ${money(st.bonus)}`} tone="good" />
            <div className="mt-2 flex justify-between border-t border-silver-100 pt-2 text-base font-bold">
              <span>To spend</span>
              <span className="num">{money(st.redeemable)}</span>
            </div>
          </div>
          {Number(account.wastage_discount_pct) > 0 || Number(account.making_discount_pct) > 0 ? (
            <p className="rounded-md bg-gold-50 px-3 py-2 text-2xs">
              This scheme also promises{' '}
              {[
                Number(account.wastage_discount_pct) > 0 ? `${account.wastage_discount_pct}% off wastage` : '',
                Number(account.making_discount_pct) > 0 ? `${account.making_discount_pct}% off making` : '',
              ]
                .filter(Boolean)
                .join(' and ')}{' '}
              on the purchase — give it as a discount on the bill.
            </p>
          ) : null}
          <p className="text-2xs text-ink-soft">
            The amount moves from the scheme deposit to the customer’s advance. The bonus is booked as the cost of the
            scheme, not hidden in the sale.
          </p>
        </div>
      ) : null}

      {mode === 'break' ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-silver-100 p-3 text-sm">
            <Row label={`Paid (${account.paid_count} of ${account.instalments.length})`} value={money(st.paid)} />
            <Row
              label={`Deduction (${account.break_penalty_pct}%)`}
              value={`− ${money(penalty.toFixed(2))}`}
              tone={penalty > 0 ? 'bad' : undefined}
            />
            <Row label="Bonus" value="none when closed early" />
            <div className="mt-2 flex justify-between border-t border-silver-100 pt-2 text-base font-bold">
              <span>Refund</span>
              <span className="num">{money(refund.toFixed(2))}</span>
            </div>
          </div>
          {Number(st.breakPenalty) > 0 ? (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} />
              Waive the deduction
            </label>
          ) : null}
          <Field label="Refund as">
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['ADVANCE', 'Keep as advance'],
                  ['CASH', 'Cash'],
                  ['BANK', 'Bank transfer'],
                ] as const
              ).map(([m, label]) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setRefundMode(m)}
                  className={`rounded-md border px-3 py-1.5 text-sm ${
                    refundMode === m ? 'border-gold-500 bg-gold-50 font-medium text-gold-700' : 'border-silver-100 text-ink-soft'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Why is it being closed?" required>
            <TextInput value={reason} onChange={setReason} maxLength={300} placeholder="Customer moving away, needs the money…" />
          </Field>
        </div>
      ) : null}

      {mode === 'undo' && lastPaid ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-silver-100 p-3 text-sm">
            <Row label="Instalment" value={String(lastPaid.instalment_no)} />
            <Row label="Paid on" value={lastPaid.paid_on?.slice(0, 10) ?? ''} />
            <Row label="Amount" value={money(lastPaid.paid_amount)} />
            <Row label="Mode" value={`${lastPaid.pay_mode ?? ''} ${lastPaid.reference_no ?? ''}`} />
            <Row label="Receipt" value={lastPaid.receipt_no ?? ''} />
          </div>
          <p className="text-2xs text-ink-soft">
            Only the latest payment can be undone. It is reversed in the books — the day book will show the money
            came in and went back out — and the instalment is due again.
          </p>
          <Field label="Why?" required>
            <TextInput value={reason} onChange={setReason} maxLength={300} placeholder="Cheque bounced, entered on the wrong account…" />
          </Field>
        </div>
      ) : null}
    </Drawer>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-ink-soft">{label}</span>
      <span className={`num ${tone === 'good' ? 'text-success' : tone === 'bad' ? 'text-danger' : ''}`}>{value}</span>
    </div>
  );
}
